// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {
    getClassName,
    modifiedDate
} from '../../services/utilities';
import {
    getRepairedDateErrorMessage,
    getVsrMessage,
    hasVsrErrorForClaimField,
    isRepairDateAfterFailureDate,
    isResponsibleAccountEditable,
    isValidateButtonShown,
    useAmountIndexToChange
} from '../../../shared/services/claim-shared-helper';
import AmountOfUseField from '../../../shared/components/claim-fields/amount-of-use';
import BatteryClaim from '@deere/cps-it.warranty-model-factory/lib/claim-battery';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimRgaStatusIndicator from '../../../shared/components/claim-rga-status-indicator';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../shared/components/claim-fields/labeled-key-value-dropdown';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../shared/components/claim-fields/responsible-accounts';
import {formatPin} from '../../../../shared/services/format-pin';
import {isValidClaim} from '../../services/claim-validation-helper';
import moment from 'moment';

function handleInputBlur(property, value) {

    this.actions.updateBatteryClaimInputOnBlur(property, value, this.claim);

}

function resetCustomerProductDetails() {

    this.actions.resetCkcCustomerFailure();
    this.actions.resetBatteryPinDetails();

}

function handlePinBlur(pin) {

    this::handleInputBlur('pin', pin);

    if (pin) {

        if (pin !== this.product.pin) {

            this::resetCustomerProductDetails();
            this.actions.readClaimProduct(pin, true);

        }

    } else {

        this::resetCustomerProductDetails();

    }

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts, this.claim.pin);

}

function handleUseAmountChange(amount, index) {

    const indexToChange = useAmountIndexToChange(this.product.complianceApplies, index);

    this.actions.updateBatteryClaimUseAmount(amount, indexToChange);

}

function handleUnitOptionChange(key) {

    this.actions.updateBatteryClaimUnitDropDown(key, this.product.useAmountAllowedUnits);

}

function handleWorkOrderOneChange(workOrder) {

    this.actions.updateBatteryClaimWorkOrderOne(workOrder);

}

function handleFailurePartChange(partNumber) {

    this.actions.updateBatteryClaimFailurePart(partNumber);

}

function handleInstalledByChange(event) {

    this.actions.updateInstalledByDropDown(event.target.value, this.installedBy);

}

function handleFailureDateChange(date) {

    this.actions.updateBatteryClaimFailureDate(date);

}

function handleRepairDateChange(date) {

    this.actions.updateBatteryClaimRepairDate(date);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'VALIDATE',
        id: 'VALIDATE',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}

function handleOwnershipStatusChange(event) {

    this.actions.updateOwnershipStatusDropDown(event.target.value, this.ownershipStatus);

}

function handleDropDownBlur(property, event) {

    this.actions.updateBatteryClaimInputOnBlur(property, event.target.value, this.claim);

}

function handlePinChange(pin) {

    this.actions.updateBatteryClaimPin(formatPin(pin));

}

function handleWorkOrderTwoChange(workOrder) {

    this.actions.updateBatteryClaimWorkOrderTwo(workOrder);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updateBatteryClaimRepairNumber(repairNumber);

}

function handleClaimPropertyChange(property, value) {

    this.actions.updateBatteryClaimProperty(property, value);

}

function handleDateBlur(property, date) {

    const dateToSend = modifiedDate(date.target.value);

    this.actions.updateBatteryClaimInputOnBlur(property, dateToSend, this.claim);

}

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <div className='claim-details-section-header'>
                <h3>
                    <FormattedMessage id='PRODUCT_INFORMATION'/>
                </h3>
                {props.claim.rgaStatus.status && props.claim.rgaStatus.status !== 'NONE' &&
                    <ClaimRgaStatusIndicator
                        claim={props.claim.claimNumber}
                        claimRgaStatus={props.claim.rgaStatus}
                    />
                }
            </div>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledKeyValueDropDown
                    className='installed-by'
                    errorMessage='INSTALLED_BY_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'installedBy', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.installedBy}
                    label='INSTALLED_BY'
                    list={props.installedBy}
                    onBlur={handleDropDownBlur.bind(props, 'installedBy')}
                    onChange={props::handleInstalledByChange}
                    selectedValue={props.claim.installedBy}
                    vsrMessage={getVsrMessage(props.claim.errors, 'installedBy', props.isEditable)}
                />
            </div>
            <div className='battery-info'>
                <LabeledInputWithBlur
                    className={`pin ${getClassName(props.claim.installedBy.key)}`}
                    errorMessage='PIN_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                    isEditable={props.isEditable && (props.claim.claimStatus.key === 'ZDFT' || !props.claim.claimStatus.key)}
                    isValid={props.formValidation.pin}
                    label='PIN'
                    maxLength={18}
                    onBlur={props::handlePinBlur}
                    onChange={props::handlePinChange}
                    value={props.claim.pin}
                    vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                />
                {props.claim.installedBy.key === 'R' &&
                    <div className='replacement-info'>
                        <LabeledInput
                            className='model'
                            highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'model', props.isEditable)}
                            isEditable={props.isEditable}
                            label='MODEL'
                            maxLength={5}
                            onChange={handleClaimPropertyChange.bind(props, 'model')}
                            value={props.claim.model}
                            vsrMessage={getVsrMessage(props.claim.errors, 'model', props.isEditable)}
                        />
                        <LabeledInput
                            className='serial-number'
                            highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'serialNumber', props.isEditable)}
                            isEditable={props.isEditable}
                            label='SERIAL_NUMBER'
                            maxLength={6}
                            onChange={handleClaimPropertyChange.bind(props, 'serialNumber')}
                            value={props.claim.serialNumber}
                            vsrMessage={getVsrMessage(props.claim.errors, 'serialNumber', props.isEditable)}
                        />
                    </div>
                }
            </div>
            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={props::handleWorkOrderOneChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order-two'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={props::handleWorkOrderTwoChange}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledKeyValueDropDown
                    className={`ownership-status ${getClassName(props.claim.installedBy.key)}`}
                    errorMessage='OWNERSHIP_STATUS_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'ownershipStatus', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.ownershipStatus}
                    label='OWNERSHIP_STATUS'
                    list={props.ownershipStatus}
                    onBlur={handleDropDownBlur.bind(props, 'ownershipStatus')}
                    onChange={props::handleOwnershipStatusChange}
                    selectedValue={props.claim.referenceInfo.ownershipStatus}
                    vsrMessage={getVsrMessage(props.claim.errors, 'ownershipStatus', props.isEditable)}
                />
            </div>
            {props.claim.installedBy.key === 'R' &&
                <div className='replacement-fields'>
                    <LabeledInput
                        className='previous-invoice-number'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                        isEditable={props.isEditable}
                        label='PREVIOUS_INVOICE_NUMBER'
                        maxLength={15}
                        onChange={handleClaimPropertyChange.bind(props, 'previousInvoiceNumber')}
                        value={props.claim.previousInvoiceNumber}
                        vsrMessage={getVsrMessage(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                    />
                    <LabeledDateInput
                        className='retail-install-date'
                        date={props.claim.retailInstallDate}
                        errorMessage='RETAIL_INSTALL_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'retailInstallDate', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={props.formValidation.retailInstallDate}
                        label='RETAIL_INSTALL'
                        maxDate={moment()}
                        onBlur={handleDateBlur.bind(props, 'retailInstallDate')}
                        onDateChange={handleClaimPropertyChange.bind(props, 'retailInstallDate')}
                        vsrMessage={getVsrMessage(props.claim.errors, 'retailInstallDate', props.isEditable)}
                    />
                </div>
            }
            <div className='failure-info'>
                <LabeledDateInput
                    className='failure-date'
                    date={props.claim.failureInfo.failureDate}
                    errorMessage='FAILED_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failureDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failureDate}
                    label='FAILED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'failureDate')}
                    onDateChange={props::handleFailureDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failureDate', props.isEditable)}
                />
                <LabeledDateInput
                    className='repair-date'
                    date={props.claim.failureInfo.repairDate}
                    errorMessage={getRepairedDateErrorMessage(props.claim.failureInfo.repairDate)}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.repairDate && isRepairDateAfterFailureDate(props.claim.failureInfo.repairDate, props.claim.failureInfo.failureDate)}
                    label='REPAIRED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'repairDate')}
                    onDateChange={props::handleRepairDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairDate', props.isEditable)}
                />
                {props.product.useAmountAllowedUnits.length > 0 &&
                    <AmountOfUseField
                        allowedUnits={props.product.useAmountAllowedUnits}
                        className='amount-of-use'
                        complianceApplies={props.product.complianceApplies}
                        errors={props.claim.errors}
                        formValidation={props.formValidation}
                        isEditable={props.isEditable}
                        onInputBlur={handleInputBlur.bind(props, 'useAmount')}
                        onInputChange={props::handleUseAmountChange}
                        onOptionBlur={handleInputBlur.bind(props, 'useAmountUnit')}
                        onOptionChange={props::handleUnitOptionChange}
                        useAmounts={props.claim.useAmounts}
                    />
                }
                <LabeledInputWithBlur
                    className='failure-part'
                    errorMessage='FAILED_PART_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failurePart}
                    label='FAILED_PART'
                    maxLength={18}
                    onBlur={handleInputBlur.bind(props, 'failurePart')}
                    onChange={props::handleFailurePartChange}
                    value={props.claim.failureInfo.failurePartNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                />
            </div>
            {isValidateButtonShown(props.isEditable, props.claim.showValidate) &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim, props.product.useAmountAllowedUnits.length)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(BatteryClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    installedBy: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    isEditable: PropTypes.bool.isRequired,
    ownershipStatus: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/battery/components/claim-details-section/claim-details-container.js